import { bitbucketClient } from "./bitbucketCore";

const normalizeRepository = (repo) => ({
  ...repo,
  // Ensure project data is always available (even if null)
  project: repo.project || null,
});

const normalizeWorkspace = (workspaceAccess) => {
  const workspace = workspaceAccess.workspace || workspaceAccess;
  const slug = workspace.slug || workspace.name || workspace.uuid;

  return {
    ...workspace,
    slug,
    name: workspace.name || workspace.slug || slug,
    administrator:
      workspaceAccess.administrator ?? workspace.administrator ?? false,
    permission: workspaceAccess.permission ?? workspace.permission,
  };
};

const fetchPaginatedValues = async (initialUrl) => {
  let url = initialUrl;
  let values = [];
  let hasNextPage = true;

  while (hasNextPage) {
    const response = await bitbucketClient.get(url);
    values = [...values, ...(response.data.values || [])];

    if (response.data.next) {
      url = response.data.next;
    } else {
      hasNextPage = false;
    }
  }

  return values;
};

const getRepositoriesForWorkspace = async (workspace) => {
  const repositories = await fetchPaginatedValues(
    `/repositories/${workspace}?pagelen=100`
  );

  return repositories.map(normalizeRepository);
};

// Repository API functions
export const getUserRepositories = async (workspace = null) => {
  try {
    if (workspace) {
      return getRepositoriesForWorkspace(workspace);
    }

    const workspaces = await getWorkspaces();
    const repositoriesByWorkspace = await Promise.all(
      workspaces.map((workspace) =>
        getRepositoriesForWorkspace(workspace.slug)
      )
    );

    return repositoriesByWorkspace.flat();
  } catch (error) {
    console.error("Error fetching repositories:", error);
    throw error;
  }
};

// Workspace API
export const getWorkspaces = async () => {
  try {
    const workspaces = await fetchPaginatedValues(
      "/user/workspaces?pagelen=100"
    );
    return workspaces.map(normalizeWorkspace);
  } catch (error) {
    console.error("Error fetching workspaces:", error);
    throw error;
  }
};

// Get repository branches
export const getRepositoryBranches = async (workspace, repoSlug) => {
  try {
    console.log(`API: Fetching branches for ${workspace}/${repoSlug}`);

    if (!workspace || !repoSlug) {
      console.error("Missing required parameters for getRepositoryBranches:", {
        workspace,
        repoSlug,
      });
      throw new Error("Workspace and repository slug are required");
    }

    let url = `/repositories/${workspace}/${repoSlug}/refs/branches?pagelen=100`;

    let allBranches = [];
    let hasNextPage = true;

    // Handle pagination by fetching all pages
    while (hasNextPage) {
      console.log(`API: Fetching branch page: ${url}`);
      const response = await bitbucketClient.get(url);

      if (response.data && response.data.values) {
        console.log(
          `API: Found ${response.data.values.length} branches on this page`
        );
        allBranches = [...allBranches, ...response.data.values];

        // Check if there's a next page
        if (response.data.next) {
          // Use the full URL from the response for the next page
          url = response.data.next;
        } else {
          hasNextPage = false;
        }
      } else {
        console.warn("API: Unexpected response format:", response.data);
        hasNextPage = false;
      }
    }

    console.log(`API: Total branches found: ${allBranches.length}`);
    return allBranches;
  } catch (error) {
    console.error("Error fetching repository branches:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
};

// Get bitbucket-pipelines.yml file content
export const getRepositoryPipelineYAML = async (workspace, repoSlug) => {
  try {
    console.log(`API: Fetching pipelines.yml for ${workspace}/${repoSlug}`);

    if (!workspace || !repoSlug) {
      console.error(
        "Missing required parameters for getRepositoryPipelineYAML:",
        {
          workspace,
          repoSlug,
        }
      );
      throw new Error("Workspace and repository slug are required");
    }

    // The path to the pipelines file in the repository
    const path = "bitbucket-pipelines.yml";

    // Get the file content from the repository's default branch
    const url = `/repositories/${workspace}/${repoSlug}/src/HEAD/${path}`;
    const response = await bitbucketClient.get(url);

    if (response.data) {
      return response.data;
    } else {
      throw new Error("Could not fetch pipeline YAML content");
    }
  } catch (error) {
    console.error("Error fetching repository pipeline YAML:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
};

// Parse pipelines from YAML content
export const getRepositoryPipelineFromYAML = async (workspace, repoSlug) => {
  try {
    console.log(`API: Parsing pipelines for ${workspace}/${repoSlug}`);

    // Get the YAML content
    const yamlContent = await getRepositoryPipelineYAML(workspace, repoSlug);

    // Since we're dealing with plain text response, we need to parse it
    // This requires a YAML parser in the project
    // For now, we'll return a structured object based on the expected format

    // Simple regex-based parsing for demonstration
    // In production, use a proper YAML parser
    const pipelinesMatch = yamlContent.match(
      /pipelines:\s*\n([\s\S]*?)(?:\n\w|$)/
    );
    const branchesMatch = yamlContent.match(
      /branches:\s*\n([\s\S]*?)(?:\n\w|$)/
    );

    const result = {
      pipelines: {},
      branches: {},
    };

    // Parse pipeline sections if they exist
    if (pipelinesMatch && pipelinesMatch[1]) {
      const pipelineSection = pipelinesMatch[1];
      // Extract pipeline names (this is a simplified approach)
      const pipelineRegex = /(\s{2,})([^:\s]+):\s*\n/g;
      let match;
      while ((match = pipelineRegex.exec(pipelineSection)) !== null) {
        result.pipelines[match[2]] = true;
      }
    }

    // Parse branches section if it exists
    if (branchesMatch && branchesMatch[1]) {
      const branchesSection = branchesMatch[1];
      // Extract branch pipeline names (this is a simplified approach)
      const branchRegex = /(\s{2,})([^:\s]+):\s*\n/g;
      let match;
      while ((match = branchRegex.exec(branchesSection)) !== null) {
        result.branches[match[2]] = true;
      }
    }

    return result;
  } catch (error) {
    console.error("Error parsing repository pipeline from YAML:", error);
    throw error;
  }
};

const repositoryApi = {
  getUserRepositories,
  getWorkspaces,
  getRepositoryBranches,
  getRepositoryPipelineYAML,
  getRepositoryPipelineFromYAML,
};

export default repositoryApi;
